import React, { useState, useEffect } from 'react';
import axios from '../api/axiosInstance';
import ParkingMapOnSpotView from './ParkingMapOnSpotView';
import './ParkingSpotsView.css';

function ParkingSpotsView({ currentUserId }) {
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [weekRange, setWeekRange] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [fullyOccupiedSpots] = useState([]);  // Nowy stan dla pełnych miejsc
  const [spotOccupancyPercentage, setSpotOccupancyPercentage] = useState(0);
  const [userCarNumbers, setUserCarNumbers] = useState({});
  const HOST = process.env.REACT_APP_HOST;
  const PORT = process.env.REACT_APP_API_PORT;
  const url = `https://${HOST}`;

  // Funkcja do obliczania zakresów poniedziałek-piątek na dwa tygodnie
  const calculateWeekRange = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const mondayThisWeek = new Date(today);
    mondayThisWeek.setDate(today.getDate() + daysToMonday);
    mondayThisWeek.setHours(12, 0, 0, 0);

    const weekRanges = [];
    for (let i = 0; i < 4; i++) {
      const startDate = new Date(mondayThisWeek);
      startDate.setDate(mondayThisWeek.getDate() + i * 14); // Co dwa tygodnie
      
      let workDays = [];
      let tempDate = new Date(startDate);
      while (workDays.length < 10) {
        if (tempDate.getDay() >= 1 && tempDate.getDay() <= 5) { // Pomijamy soboty i niedziele
          workDays.push(new Date(tempDate));
        }
        tempDate.setDate(tempDate.getDate() + 1);
      }

      weekRanges.push({
        startDate: workDays[0],
        endDate: workDays[workDays.length - 1],
        label: `${workDays[0].toLocaleDateString()} - ${workDays[workDays.length - 1].toLocaleDateString()}`
      });
    }

    return weekRanges;
  };

  useEffect(() => {
    const ranges = calculateWeekRange();
    setWeekRange(ranges);
    setSelectedWeek(ranges[0]);
  }, []);

  const handleSelectWeek = (event) => {
    const selected = weekRange.find(range => range.label === event.target.value);
    setSelectedWeek(selected);
  };

  const [availabilityBySpot, setAvailabilityBySpot] = useState({});
  const [allSpots, setAllSpots] = useState([]);
  
  useEffect(() => {
    if (!selectedWeek) return;
  
    const fetchAllData = async () => {
      try {
        const spotsRes = await axios.get(`${url}/api/admin/parking-spots`);
        const spots = spotsRes.data;
        setAllSpots(spots);
  
        const spotIds = spots.map(s => s.id);
  
        const availabilityRes = await axios.get(`${url}/api/reservations/availability`, {
          params: {
            spotIds,
            startDate: selectedWeek.startDate.toISOString(),
            endDate: selectedWeek.endDate.toISOString(),
          }
        });
  
        const availabilityData = availabilityRes.data;
        setAvailabilityBySpot(availabilityData);
  
        const results = spotIds.map(spotId => {
          const days = availabilityData[spotId] || [];
          const workdays = days.filter(day => {
            const d = new Date(day.date).getDay();
            return d >= 1 && d <= 5;
          });
          const occupied = workdays.filter(d => !d.isFree).length;
          const percentage = workdays.length > 0 ? (occupied / workdays.length) * 100 : 0;
          return { spotId, occupancyPercentage: percentage };
        });
  
        setPartiallyOccupiedSpots(results);
  
      } catch (err) {
        console.error("Błąd przy pobieraniu danych zbiorczych:", err);
      }
    };
  
    fetchAllData();
  }, [selectedWeek, url]);


  const [partiallyOccupiedSpots, setPartiallyOccupiedSpots] = useState([]);

  const fetchCarNumbersBatch = async (usernames) => {
    const uniqueUsernames = [...new Set(usernames.filter(u => u && !userCarNumbers[u]))];
    if (uniqueUsernames.length === 0) return;
  
    try {
      const { data } = await axios.get(`${url}/api/user/car-numbers`, {
        params: { usernames: uniqueUsernames }
      });
  
      setUserCarNumbers(prev => ({ ...prev, ...data }));
    } catch {
      const fallback = {};
      uniqueUsernames.forEach(u => fallback[u] = "Błąd");
      setUserCarNumbers(prev => ({ ...prev, ...fallback }));
    }
  };
  useEffect(() => {
    if (!selectedSpot || !availabilityBySpot.hasOwnProperty(selectedSpot.id)) return;
  
    const spotData = availabilityBySpot[selectedSpot.id];
    setAvailability(spotData);
  
    const workdays = spotData.filter(day => {
      const dayOfWeek = new Date(day.date).getDay();
      return dayOfWeek >= 1 && dayOfWeek <= 5;
    });
  
    const occupiedDays = workdays.filter(day => !day.isFree).length;
    const occupancyPercentage = workdays.length > 0 ? (occupiedDays / workdays.length) * 100 : 0;
    setSpotOccupancyPercentage(occupancyPercentage);
  
    // 🔥 nie fetchujemy już numerów rejestracyjnych z osobnego źródła
  }, [selectedSpot, availabilityBySpot]);

  return (
    <div className="spots-container">
      <div className="week-selector-container">
        <div className="select-container">
          <label htmlFor="weekSelect">Wybierz zakres tygodni:</label>
          <select
            id="weekSelect"
            onChange={handleSelectWeek}
            value={selectedWeek ? selectedWeek.label : ''}
          >
            {weekRange.map((week) => (
              <option key={week.label} value={week.label}>
                {week.label}
              </option>
            ))}
          </select>
        </div>
      </div>
  
      <div className="parking-section">
        <h2>Miejsca Parkingowe</h2>
        <ParkingMapOnSpotView
          onSelectSpot={setSelectedSpot}
          selectedSpot={selectedSpot}
          currentUserId={currentUserId}
          fullyOccupiedSpots={fullyOccupiedSpots}
          occupancyData={partiallyOccupiedSpots}
        />
      </div>
  
      <div className="availability-section">
        <h3>
          Dostępność dla miejsca{' '}
          {selectedSpot ? `#${selectedSpot.spotNumber}` : ''}
        </h3>
  
        {availability.length > 0 ? (
          <div className="availability-details">
            {availability
              .filter((day) => {
                const dayOfWeek = new Date(day.date).getDay();
                return dayOfWeek >= 1 && dayOfWeek <= 5;
              })
              .map((day) => {
                const isFree = day.isFree;
                const isMine = !isFree && currentUserId != null && currentUserId != undefined && day.userId === currentUserId;
                
                const isOccupiedByOthers = !isFree && day.user && day.userId !== currentUserId;
              
                return (
                  <div
                    key={day.date}
                    className={`availability-day 
                      ${isFree ? 'free' : ''} 
                      ${isMine ? 'my-reservation' : ''} 
                      ${isOccupiedByOthers ? 'other-user-reservation' : ''}`}
                  >
                    <div className="day-abbr">
                      {new Date(day.date).toLocaleDateString('pl-PL', {
                        weekday: 'short',
                      })}
                    </div>
              
                    <div className="username">
                      {isFree ? 'Wolne' : day.user || '—'}
                    </div>
              
                    <div className="regcarnum">
                      {isFree ? '' : day.regCarNum || '—'}
                    </div>
                  </div>
                );
              }
            )}
          </div>
        ) : (
          <p>Wybierz miejsce, aby zobaczyć dostępność.</p>
        )}
      </div>
      <footer className="app-footer">
        <div className="footer-content">
          <img src="/xkom_ambasador.png" alt="Program ambasadorów X-KOM" className="footer-logo" />
          <span> Autor: <strong>Kamil Gajewski</strong></span>
        </div>
      </footer>
    </div>
  );
}
export default ParkingSpotsView;
